import { Component } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { ActivatedRoute } from '@angular/router';
import { ProcessingManager } from '@managers/processing.manager';
import { Observable, combineLatest, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerState } from '@models/servers/server-state.model';
import { Store } from "@ngrx/store";
import { selectUIVersion, selectVersion } from "@store/global/global.selectors";
import { animate, style, transition, trigger } from "@angular/animations";

@Component({
    selector: 'lex-platform-activity',
    templateUrl: './platform-activity.component.html',
    styleUrls: ['./platform-activity.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ marginRight: '-480px' }),
                animate('350ms ease-in-out', style({ marginRight: '0' }))
            ]),
            transition(':leave', [
                style({ marginRight: '0' }),
                animate('350ms ease-in-out', style({ marginRight: '-480px' }))
            ])
        ])
    ],
    standalone: false
})
export class PlatformActivityComponent extends BaseComponent {
	public isSearchedFocused: boolean;
	public filterText$: Observable<string>;
	public totalActive$: Observable<number>;
	public totalQueued$: Observable<number>;
	public totalIndexHealth$: Observable<number>;
	public totalServers$: Observable<number>;
	public totalMicroservices$: Observable<number>;
	public totalWorkflows$: Observable<number>;
	public currentVersion$: Observable<string> = this.store.select(selectVersion);
	public currentUIVersion$: Observable<string> = this.store.select(selectUIVersion);
	public currentPanel: 'case-priorities' | 'none';

	constructor(
		public activatedRoute: ActivatedRoute,
		private store: Store,
		private processingManager: ProcessingManager
	) {
		super();
		this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(x => {
			this.currentPanel = x['panel'] ? x['panel'] : 'none';
		})
		this.filterText$ = this.processingManager.currentFilterText$;

		this.totalQueued$ = this.processingManager.allSteps$.pipe(
			map((steps) => {
				return steps?.reduce((p, c) => {
					return p + c.stepCount;
				}, 0);
			})
		);

		this.totalActive$ = this.processingManager.activeSteps$.pipe(
			map((steps) => {
				return steps?.reduce((p, c) => {
					return p + c.stepCount;
				}, 0);
			})
		);

		this.totalIndexHealth$ = combineLatest([
			this.processingManager.outOfSync$,
			this.processingManager.kbOutOfSync$
		]).pipe(
			map(([item1, item2]) => item1?.length + item2?.length)
		);

		this.totalServers$ = this.processingManager.servers$.pipe(
			map((items) => items?.filter((x) => x.serverState.value !== ServerState.Terminated.value)?.length)
		);

		this.totalMicroservices$ = this.processingManager.ms$.pipe(
			map((items) => items?.length)
		);

		this.totalWorkflows$ = this.processingManager.workflows$.pipe(
			map((items) => items?.length)
		);
	}

	public setSearchText(searchText: string): void {
		this.processingManager.setSearchText(searchText);
	}

	public clearFilterText(): void {
		this.processingManager.setSearchText(null);
	}

	public setRowHeight(amount: number): void {
		this.processingManager.setRowHeight(amount);
	}
}
